import { isModelInsightKey } from "~/components/demand-2/shared/adjustmentImpacts"
import type { IVariantSummary } from "~/hooks/useVariantTableData"
import type { EffectiveDemand } from "~/utils/demandEffective"
import { isFramingSignalKey } from "~/utils/demandFramingSignal"
import { isStrategicInsightKey } from "~/utils/demandInsightValues"

import type { InsightRow, SetupSummary } from "./setupSummary"

/**
 * Collection-grain twin of `EffectiveDemand.setupDelta` / `onTopDelta`. `stages` carries levels
 * only (each IS a Total-table column); the deltas live here so no caller derives them twice.
 */
export const stackDeltas = (stages: SetupSummary["stages"], appliedRows: InsightRow[]) => {
  const signals = stages.setupForecast - stages.model
  // A strategic insight is the planner's decision, so its units sit with the planner's half.
  const insightUnits = appliedRows
    .filter((r) => !isStrategicInsightKey(r.key))
    .reduce((sum, r) => sum + r.units, 0)
  return {
    signals,
    insightUnits,
    // The Strategy half: Strategic adjustments, Cannibalization and strategic insights.
    setupAdjustments: signals - insightUnits,
    campaigns: stages.total - stages.manual,
  }
}

export type StrategyWaterfall = {
  baseline: number
  /** `model − baseline`; zero on most plans. */
  modelMove: number
  insights: number
  /** The Cannibalization (framing) signal rows. */
  assortment: number
  /** The strategic insights: comparable-season growth. */
  demand: number
  /** Setup rows that are neither: the remainder of the Strategy half, Strategic adjustments. */
  otherSetup: number
  /** Signal based demand. Manual forecast and Campaigns sit after it and stay out. */
  end: number
}

/**
 * The Demand signals stage split by the dialog's sections. Regroups `stackDeltas` only, so the
 * parts sum to `stages.setupForecast` exactly.
 */
export const strategyWaterfall = (
  summary: Pick<SetupSummary, "stages" | "appliedRows" | "strategicRows" | "signalRows">,
): StrategyWaterfall => {
  const { stages, appliedRows, strategicRows, signalRows } = summary
  const { insightUnits, setupAdjustments } = stackDeltas(stages, appliedRows)
  const assortment = signalRows
    .filter((r) => isFramingSignalKey(r.id))
    .reduce((sum, r) => sum + (r.units ?? 0), 0)
  const demand = strategicRows.reduce((sum, r) => sum + r.units, 0)
  return {
    baseline: stages.baseline,
    modelMove: stages.model - stages.baseline,
    insights: insightUnits,
    assortment,
    demand,
    otherSetup: setupAdjustments - assortment - demand,
    end: stages.setupForecast,
  }
}

/** Units before and after the Cannibalization signal, both measured after the model insights. */
export type CannibalizationReading = {
  /** The engine baseline alone: what a signal's rate is sized on. */
  baselineUnits: number
  beforeUnits: number
  afterUnits: number
}

/**
 * The engine folds model insights, then Cannibalization, so the signal lands on baseline +
 * insights. Read off the engine's attribution, never re-sized, so it matches the Total columns.
 */
export const cannibalizationReading = (
  variants: Iterable<IVariantSummary>,
  effective: (v: IVariantSummary) => Pick<EffectiveDemand, "baseline" | "setupAttribution">,
): CannibalizationReading => {
  let baselineUnits = 0
  let beforeUnits = 0
  let signalUnits = 0
  for (const v of variants) {
    const e = effective(v)
    baselineUnits += e.baseline
    beforeUnits += e.baseline
    for (const [key, units] of Object.entries(e.setupAttribution)) {
      if (isModelInsightKey(key)) beforeUnits += units
      else if (isFramingSignalKey(key)) signalUnits += units
    }
  }
  return { baselineUnits, beforeUnits, afterUnits: beforeUnits + signalUnits }
}
